/**
 * Owner: Masa Bayu
 * File: hub-error-copy.ts
 * Date:Time: 2026-10-22 10:56
 * Description: §457 F5 — qxp-hub bertanya dalam Bahasa Melayu, manakala paparan
 *   identiti qxread (`/login` + overlay beli) berbahasa English. Jadual ini
 *   memetakan mesej hub yang DIKETAHUI sahaja; mesej yang tidak dikenali
 *   dipulangkan apa adanya supaya pengguna dan log tidak pernah kehilangan
 *   sebab sebenar (tiada mesej generik yang menyembunyi).
 *
 *   Hub tidak disentuh — penterjemahan di sempadan paparan supaya satu sumber
 *   mesej pelayan (BM) kekal untuk qxeditor/qxwriter yang membacanya terus.
 *   §457 F6-C — tambah peta §453: 409 (`akaun masih memiliki N publication`)
 *   diisi kiraan semula, dan mesej klien "tiada sesi qxread aktif".
 */

/** `Map` (bukan `Record`) — kunci seperti 'constructor' tidak boleh padan. */
const EXACT = new Map<string, string>([
  // Laluan akaun hub (hub-accounts.mjs / hub-store.mjs)
  ['email sudah berdaftar', 'This email is already registered. Try signing in.'],
  ['email atau kata laluan salah', 'Email or password is incorrect.'],
  ['token tidak sah atau tiada', 'Your session has expired. Please sign in again.'],
  ['displayName mesti string ≤ 64 aksara', 'Name must be 64 characters or fewer.'],
  ['role tidak sah — guna "reader" atau "publisher"', 'Choose Reader or Publisher to continue.'],
  ['body bukan JSON sah', 'The sign-in service returned an unreadable response.'],
  ['akaun untuk sesi ini tidak ditemui', 'This account no longer exists.'],
  ['hanya akaun sendiri boleh dipadam', 'You can only cancel your own account.'],
  ['accountId tidak sah', 'Invalid account reference.'],
  ['akaun tidak ditemui', 'Account not found.'],
  ['kata laluan tidak sah', 'Password is incorrect.'],
  // Google (hub-google.mjs / hub-account-google.mjs)
  ['aud bukan klien Google qxread', 'That Google account is not for QXRead.'],
  ['token Google sudah luput', 'Your Google session expired. Please try again.'],
  ['token Google belum sah (nbf)', 'Your Google session is not valid yet. Check your clock.'],
  ['token Google tiada sub', 'Google did not return an account identity.'],
  ['token Google tiada email', 'Google did not return an email for this account.'],
  [
    'e-mail Google belum disahkan (email_verified=false)',
    'Confirm your Google email address first, then try again.',
  ],
  ['token Google bukan JWT 3-bahagian', 'Google returned an unreadable credential.'],
  ['header JWT tidak sah', 'Google returned an unreadable credential.'],
  ['payload JWT tidak sah', 'Google returned an unreadable credential.'],
  ['typ JWT tidak sah', 'Google returned an unreadable credential.'],
  ['header JWT tiada kid', 'Google returned a credential without a key id.'],
  ['signature JWT tidak sah', 'Google returned an invalid signature.'],
  ['kid token tiada dalam JWKS Google', 'Google signing keys changed. Please try again.'],
  ['tandatangan token Google tidak sah', 'Google rejected this credential. Please try again.'],
  [
    'email ini sudah terhubung dengan identiti Google lain',
    'This email is already linked to a different Google account.',
  ],
  ['claim Google tiada sub atau email sah', 'Google did not return a valid account identity.'],
  // Mesej dijana oleh klien hub-session.ts (bukan pelayan)
  [
    'Pelayan identiti tidak dapat dicapai',
    'The sign-in service is unavailable right now. Please try again later.',
  ],
  ['tiada sesi qxread aktif', 'You are not signed in. Sign in again to cancel your account.'],
  [
    'Respons identiti tiada token atau akaun',
    'The sign-in service returned an incomplete session.',
  ],
  ['Respons identiti tiada akaun', 'The sign-in service returned an incomplete session.'],
]);

const PREFIX = new Map<string, string>([
  ['Respons identiti bukan JSON', 'The sign-in service returned an unreadable response.'],
  ['google_jwks_tiada', 'Google is unreachable right now. Please try again in a moment.'],
  ['JWKS HTTP', 'Google is unreachable right now. Please try again in a moment.'],
  ['JWKS tiada kunci RSA', 'Google is unreachable right now. Please try again in a moment.'],
  ['Request failed (', 'The sign-in service rejected the request.'],
  // Mesej hub yang disusuli nilai berubah (`role tidak sah: …`, `iss tidak …`)
  ['role tidak sah', 'Choose Reader or Publisher to continue.'],
  ['iss tidak diizinkan', 'That Google account is not for QXRead.'],
  ['hd tidak dalam senarai diizinkan', 'This Google workspace cannot sign in to QXRead.'],
  ['alg tidak disokong', 'Google returned an unreadable credential.'],
]);

/** §453 409 — kiraan publication berubah dalam mesej hub, jadi ia diisi semula. */
const LIVE_PUBLICATIONS = /^akaun masih memiliki (\d+) publication/;

/** Mesej untuk dipapar: petakan yang dikenali, selain itu kekalkan teks asal. */
export function describeAuthError(message: string): string {
  const exact = EXACT.get(message);
  if (exact !== undefined) return exact;
  const blocked = LIVE_PUBLICATIONS.exec(message);
  if (blocked !== null) {
    return `Your account still has ${String(blocked[1])} published book(s) in the catalogue. Take them down before cancelling.`;
  }
  for (const [needle, copy] of PREFIX) {
    if (message.startsWith(needle)) return copy;
  }
  return message;
}
